import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';

import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import {
    ISTANBUL_ROTARACT_IMAGE_SRC,
    ISTANBUL_ROTARACT_WEBSITE_HREF,
    mapIstanbulRotaractPastRows,
    parseIstanbulRotaractCurrentPresident,
} from '../data/istanbulRotaractContent';

/**
 * İstanbul Rotaract Kulübü — URL: /istanbul-rotaract
 */
export function IstanbulRotaractPage() {
    const { t } = useTranslation();

    const currentPresident = useMemo(() => {
        const raw = t('istanbulRotaract.currentPresident', { returnObjects: true });
        return parseIstanbulRotaractCurrentPresident(raw);
    }, [t]);

    const pastLeft = useMemo(() => {
        const raw = t('istanbulRotaract.pastLeft', { returnObjects: true });
        return mapIstanbulRotaractPastRows(raw);
    }, [t]);

    const pastRight = useMemo(() => {
        const raw = t('istanbulRotaract.pastRight', { returnObjects: true });
        return mapIstanbulRotaractPastRows(raw);
    }, [t]);

    return (
        <>
            <PageSeo
                title={`${t('istanbulRotaract.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.istanbulRotaract')}
            />
            <SubpageLayout
                title={t('istanbulRotaract.title')}
                subtitle={t('istanbulRotaract.subtitle')}
                contentClassName="istanbul-rotaract-page"
            >
            <figure className="istanbul-rotaract-figure mb-0">
                <img
                    src={ISTANBUL_ROTARACT_IMAGE_SRC}
                    alt={t('istanbulRotaract.imageAlt')}
                    className="istanbul-rotaract-hero-img"
                    loading="lazy"
                    decoding="async"
                />
            </figure>
            <div className="istanbul-rotaract-body mt-4">
                <div className="istanbul-rotaract-intro">
                    <a
                        href={ISTANBUL_ROTARACT_WEBSITE_HREF}
                        className="istanbul-rotaract-site-link d-inline-flex align-items-center gap-2 text-decoration-none"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <ExternalLink size={18} strokeWidth={1.75} aria-hidden />
                        <span>{t('istanbulRotaract.websiteLabel')}</span>
                    </a>
                    <p className="istanbul-rotaract-foundation mb-0">
                        <span className="istanbul-rotaract-foundation-label">
                            {t('istanbulRotaract.foundationLabel')}
                        </span>
                        <span className="text-secondary">: </span>
                        {t('istanbulRotaract.foundationValue')}
                    </p>
                </div>

                <section
                    className="istanbul-rotaract-president mt-4 pt-2"
                    aria-labelledby="istanbul-rotaract-president-heading"
                >
                    <h2
                        id="istanbul-rotaract-president-heading"
                        className="istanbul-rotaract-section-title h5 mb-3"
                    >
                        {t('istanbulRotaract.presidentSectionTitle')}
                    </h2>
                    <p className="istanbul-rotaract-president-line mb-0">
                        <span className="istanbul-rotaract-term">
                            {currentPresident.term}
                        </span>
                        <span className="istanbul-rotaract-name">
                            {currentPresident.name}
                        </span>
                    </p>
                </section>

                <section
                    className="istanbul-rotaract-past mt-4 pt-3"
                    aria-labelledby="istanbul-rotaract-past-heading"
                >
                    <h2
                        id="istanbul-rotaract-past-heading"
                        className="istanbul-rotaract-section-title h5 mb-3"
                    >
                        {t('istanbulRotaract.pastSectionTitle')}
                    </h2>
                    <div className="row g-4 g-md-5">
                        <div className="col-md-6">
                            <ul className="istanbul-rotaract-past-list list-unstyled mb-0">
                                {pastLeft.map((row) => (
                                    <li
                                        key={`${row.term}-${row.name}`}
                                        className="istanbul-rotaract-past-item"
                                    >
                                        <span className="istanbul-rotaract-term">
                                            {row.term}
                                        </span>
                                        <span className="istanbul-rotaract-name">
                                            {row.name}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="col-md-6">
                            <ul className="istanbul-rotaract-past-list list-unstyled mb-0">
                                {pastRight.map((row) => (
                                    <li
                                        key={`${row.term}-${row.name}`}
                                        className="istanbul-rotaract-past-item"
                                    >
                                        <span className="istanbul-rotaract-term">
                                            {row.term}
                                        </span>
                                        <span className="istanbul-rotaract-name">
                                            {row.name}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            </div>
        </SubpageLayout>
        </>
    );
}
